import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Check, ArrowRight, Star, MapPin, PenTool, BookOpen, TrendingUp, Mic, FileEdit, Printer, Palette, Sparkles } from "lucide-react";
import { testimonials, portfolioProjects, services } from "../../mock";

const iconMap = {
  PenTool: PenTool,
  BookOpen: BookOpen,
  TrendingUp: TrendingUp,
  Mic: Mic,
  FileEdit: FileEdit,
  Printer: Printer,
  Palette: Palette,
  Sparkles: Sparkles
};
import SEO from "../../components/SEO";
import ServicePopup from "../../components/ServicePopup";
import { openChat } from "../../utils/chatUtils";
import countryCodes from "../../utils/countryCodes";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import AnimatedIcon from "../../components/AnimatedIcon";
import AOS from "aos";
import "aos/dist/aos.css";

const WebContentWriting = () => {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    countryCode: "+1",
    message: ""
  });

  // Use centralized form submission hook
  const { isSubmitting, formStatus, handleSubmit, geoData, isLoadingGeo } = useFormSubmit();

  const [activeGenre, setActiveGenre] = useState("Children\'s Book");
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupService, setPopupService] = useState({ title: "", description: "" });

  const genres = ["Children\'s Book", "Fiction", "Memoir", "Non-Fiction", "Poetry"];
  const filteredProjects = portfolioProjects.filter(project => project.genre === activeGenre);

  const openServicePopup = (title, description) => {
    setPopupService({ title, description });
    setPopupOpen(true);
  };

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100
    });

    window.scrollTo(0, 0);
    // Parallax effect for hero background
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const heroBackground = document.querySelector(".hero-background");
      if (heroBackground) {
        heroBackground.style.transform = `translateY($\{scrolled * 0.5}px)`;
      }
    };

    window.addEventListener("scroll", handleScroll);
    
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => {
        const maxIndex = Math.ceil(testimonials.length / 3) - 1;
        return prevIndex === maxIndex ? 0 : prevIndex + 1;
      });
    }, 5000);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(interval);
    };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(contactForm);
    
    // Reset form only on success
    if (formStatus.type === "success") {
      setContactForm({ 
        name: "", 
        email: "", 
        phone: "", 
        countryCode: "+1",
        message: ""
      });
    }
  };


  const processSteps = [
    {
      number: "01",
      title: "Discovery & Research",
      description: "We work with you to understand your book\'s story, themes, and target audience to create the perfect trailer concept."
    },
    {
      number: "02",
      title: "Content Writing",
      description: "Compelling copy creation optimized for search engines and designed to convert visitors."
    },
    {
      number: "03",
      title: "Optimization & Delivery",
      description: "Meta tags, CTAs, and final polish to ensure maximum impact and conversions."
    }
  ];

  return (
    <div className="min-h-screen bg-[#000000]">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="hero-background">
          <img
            src="https://images.unsplash.com/photo-1507208773393-40d9fc670acf?w=1920"
            alt="Service Background"
            className="hero-image"
          />
          <div className="hero-overlay-split"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div data-aos="fade-right">
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                WEB CONTENT WRITING SERVICE
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                SEO-optimized website copy that attracts visitors and converts them into customers.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#F4D03F] flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Homepage & service pages</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#F4D03F] flex-shrink-0 mt-1" />
                  <p className="text-gray-300">SEO optimization</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#F4D03F] flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Conversion-focused copy</p>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#F4D03F] flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Brand voice matching</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary"
                  onClick={() => openServicePopup("Start Your Web Content Writing Journey", "Get started with our professional Web Content Writing services. Fill out the form to receive a free consultation and custom quote.")}
                >
                  Write Your Content <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  className="js-chat btn-secondary"
                  onClick={openChat}
                >
                  Discuss Your Project
                </Button>
              </div>
            </div>

            {/* Right Side - Contact Form */}
            <div className="hero-form-container" data-aos="fade-left">
              <Card className="bg-gradient-to-br from-[#2C3E50] to-[#1a1a1a] border-2 border-[#F4D03F] shadow-2xl backdrop-blur-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#F4D03F] opacity-10 rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#F8D26B] opacity-10 rounded-tr-full"></div>
                
                <CardHeader className="relative z-10 p-3 sm:p-5">
                  <CardTitle className="text-[#F4D03F] text-2xl sm:text-3xl font-bold uppercase text-center mb-2">
                    HIGH-CONVERTING WEB CONTENT THAT RANKS
                  </CardTitle>
                  <CardDescription className="text-[#ffffff] text-center" style={{ fontSize: "1.25rem" }}>
                    Strategic content that dominates search results and transforms visitors into loyal customers.
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10">
                  <form onSubmit={handleContactSubmit} className="space-y-2 sm:space-y-3">
                    <div>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="your.email@example.com"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    <div>
                      <div className="flex gap-2">
                        <select aria-label="Country Code"
                          value={contactForm.countryCode}
                          onChange={(e) => setContactForm({ ...contactForm, countryCode: e.target.value })}
                          className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] rounded-lg px-2 py-2 focus:outline-none focus:border-[#F4D03F] transition-all duration-300 hover:border-[#F8D26B]"
                          style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.country} {country.code}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          placeholder="(123) 456-7890"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          required
                          className="flex-1 bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] h-10 sm:h-12 text-sm sm:text-base rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                        />
                      </div>
                    </div>
                    <div>
                      <Textarea aria-label="Your Message"
                        placeholder="Tell us about your website..."
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        rows={4}
                        className="bg-[#000000] border-2 border-[#2C3E50] text-[#ffffff] placeholder:text-[#888680] focus:border-[#F4D03F] text-lg rounded-lg transition-all duration-300 hover:border-[#F8D26B]"
                      />
                    </div>
                    <Button type="submit" className="btn-primary w-full h-10 sm:h-12 text-sm sm:text-base font-bold shadow-lg hover:shadow-2xl transition-all duration-300">
                      Start Your Project →
                    </Button>
                    <div className="mt-3 flex flex-col items-center"><div className="w-[40%] h-[1px] bg-white/8 mb-2"></div><p className="text-center font-semibold shimmer-text" style={{ fontSize: "1.25rem", color: "#E0BF5A", opacity: 0.95, textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)", letterSpacing: "0.2px" }}>✓ Free consultation • ✓ No obligation • ✓ Expert guidance</p></div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section with Image */}
      <section className="py-16 sm:py-20 bg-[#000000] relative overflow-hidden">
        <AnimatedIcon icon="trending" position="bottom-right" size={95} delay={1.8} opacity={0.06} />
        
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-6">
                Web Content That Converts Visitors Into Customers
              </h2>
              
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Engage your website visitors with compelling copy that not only ranks well in search engines but also persuades readers to take action and become loyal customers.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">SEO-optimized website copy that ranks on Google</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">Persuasive landing pages designed to convert</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">Engaging About Us, Services, and product descriptions</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F4D03F] mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300">Brand voice development for consistency</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary"
                  onClick={() => openServicePopup("Start Your Web Content Writing Journey", "Get started with our professional Web Content Writing services. Fill out the form to receive a free consultation and custom quote.")}
                >
Write Your Content
                </Button>
                <Button 
                  className="btn-secondary"
                  onClick={openChat}
                >
                  Chat With Expert
                </Button>
              </div>
            </div>

            <div data-aos="fade-left" className="relative">
              <div className="relative rounded-lg overflow-hidden shadow-2xl border-2 border-[#F4D03F]/20 h-[500px] lg:h-[550px]">
                <img 
                  src="https://images.pexels.com/photos/16023919/pexels-photo-16023919.jpeg?w=800"
                  alt="Web Content Writing"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/50 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#F4D03F] opacity-10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>


      {/* Process Section */}
      <section id="process-section" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Our Proven Process
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              A streamlined approach that turns your vision into reality
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="relative"
              >
                <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-[#2C3E50] hover:border-[#F4D03F] transition-all duration-300 h-full">
                  <CardContent className="p-8">
                    <div className="text-6xl font-bold text-[#F4D03F] mb-4 opacity-20">
                      {step.number}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-[#F4D03F]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-16 sm:py-20 md:py-24 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">OUR PORTFOLIO</h2>
            <p className="text-base sm:text-lg text-[#ffffff] max-w-2xl mx-auto">
              Browse our collection of published books across different genres. Each book represents our dedication to quality publishing.
            </p>
          </div>

          {/* Genre Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12" data-aos="fade-up" data-aos-delay="100">
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setActiveGenre(genre)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all duration-300 ${
                  activeGenre === genre
                    ? "bg-[#F4D03F] text-[#000000] shadow-lg scale-105"
                    : "bg-[#2C3E50] text-[#ffffff] hover:bg-[#F8D26B] hover:text-[#000000]"
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="portfolio-book-card group"
                data-aos="zoom-in"
                data-aos-delay={index * 50}
              >
                <Card className="bg-[#2C3E50] border-[#F8D26B] border-opacity-30 overflow-hidden h-full hover:border-[#F4D03F] transition-all duration-500 hover:shadow-2xl hover:shadow-[#F4D03F]/20">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center p-4 sm:p-6">
                      <h3 className="text-[#F4D03F] font-bold text-base sm:text-xl mb-2 text-center">{project.title}</h3>
                      <p className="text-[#ffffff] text-xs sm:text-sm text-center">{project.description}</p>
                    </div>
                    {/* Genre Badge */}
                    <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-[#F8D26B] text-[#000000] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold uppercase">
                      {project.genre}
                    </div>
                  </div>
                  <CardContent className="p-3 sm:p-6">
                    <h3 className="text-[#ffffff] font-bold text-sm sm:text-lg mb-2 group-hover:text-[#F4D03F] transition-colors truncate">
                      {project.title}
                    </h3>
                    <div className="flex justify-between items-center text-xs sm:text-sm text-[#F4D03F]">
                      <span className="flex items-center gap-1 truncate">
                        <Check size={12} className="text-[#F8D26B] flex-shrink-0" />
                        <span className="truncate">{project.author}</span>
                      </span>
                      <span className="flex-shrink-0 bg-[#F4D03F]/20 text-[#F4D03F] px-2 py-0.5 rounded text-xs font-semibold">
                        {project.status || "Published"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR SERVICES Section */}
      <section id="services" className="py-16 sm:py-20 md:py-24 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">OUR SERVICES</h2>
            <p className="text-base sm:text-lg text-[#888680] max-w-2xl mx-auto">
              Comprehensive publishing solutions tailored to your needs
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300 hover:transform hover:scale-105 card-animate flex flex-col h-[450px] sm:h-[500px] overflow-hidden"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <CardHeader className="flex-shrink-0">
                    <div 
                      className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-full flex items-center justify-center mb-4 service-icon-animated"
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      {service.gifUrl ? (
                        <img 
                          src={service.gifUrl} 
                          alt={service.title}
                          className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                        />
                      ) : (
                        <IconComponent className="text-[#000000]" size={24} />
                      )}
                    </div>
                    <CardTitle className="text-[#F4D03F] text-lg sm:text-xl font-bold mb-2">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-[#888680] text-sm sm:text-base leading-snug">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col pt-4 overflow-hidden min-h-0">
                    <ul className="space-y-2 overflow-y-auto scrollbar-thin pr-2 flex-shrink mb-4">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[#dfddd6] text-xs sm:text-sm">
                          <Check size={14} className="text-[#F4D03F] flex-shrink-0 mt-1" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto flex flex-col gap-2 flex-shrink-0">
                      <a href={`/services/${service.slug}`} className="w-full">
                        <Button 
                          className="btn-primary w-full text-xs sm:text-sm"
                        >
                          Learn More
                        </Button>
                      </a>
                      <Button 
                        onClick={openChat}
                        className="js-chat btn-secondary w-full text-xs sm:text-sm"
                      >
                        Discuss Your Project
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1a1a] to-[#000000]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-[#F4D03F] mb-6">Skyrocket Your Online Presence</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get content that not only ranks on page one but turns clicks into revenue.
          </p>
          <Button 
            className="bg-[#F4D03F] text-black hover:bg-[#F8D26B] font-bold px-10 py-6 text-xl"
            onClick={() => openServicePopup("Start Your Web Content Project", "Ready to create compelling web content that ranks and converts? Fill out the form below and our expert writers will craft SEO-optimized content that drives results.")}
          >
            Start Your Project Today
          </Button>
          <div className="mt-3 flex flex-col items-center"><div className="w-[40%] h-[1px] bg-white/8 mb-2"></div><p className="text-center font-semibold shimmer-text" style={{ fontSize: "1.25rem", color: "#E0BF5A", opacity: 0.95, textShadow: "0 1px 2px rgba(0, 0, 0, 0.6)", letterSpacing: "0.2px" }}>✓ Free consultation • ✓ No obligation • ✓ Expert guidance</p></div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-[#302f2c]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">CLIENT SUCCESS STORIES</h2>
            <p className="text-xl text-gray-400">Success stories from our published authors</p>
          </div>

          {/* Testimonials Slider */}
          <div className="relative mb-12">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }}
              >
                {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, slideIndex) => (
                  <div key={slideIndex} className="min-w-full">
                    <div className="grid md:grid-cols-3 gap-8 px-4">
                      {testimonials.slice(slideIndex * 3, slideIndex * 3 + 3).map((testimonial) => (
                        <Card key={testimonial.id} className="bg-[#000000] border-[#3f4816]">
                          <CardContent className="pt-6">
                            <div className="flex gap-1 mb-4">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star key={i} size={20} className="fill-[#F4D03F] text-[#F4D03F]" />
                              ))}
                            </div>
                            <p className="text-[#dfddd6] mb-6 italic">"{testimonial.content}"</p>
                            <div>
                              <div className="text-[#F4D03F] font-bold">{testimonial.author}</div>
                              <div className="text-[#888680] text-sm">{testimonial.role}</div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Slider Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonialIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentTestimonialIndex === index 
                      ? "bg-[#F4D03F] w-8" 
                      : "bg-[#888680] hover:bg-[#F8D26B]"
                  }`}
                  aria-label={`Go to testimonial slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Popup */}
      <ServicePopup 
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        service={popupService}
      />
    </div>
  );
};

export default WebContentWriting;
